import { Shield, Target, TrendingUp, Coins, DollarSign, UserPlus, Briefcase, Users, Activity, Database, Video, Settings, Home, AlertCircle, MessageSquare, Share2 } from 'lucide-react';
import { NavLink } from 'react-router-dom';
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuItem,
  useSidebar,
} from '@/components/ui/sidebar';
import { cn } from '@/lib/utils';

// Organized admin navigation sections
const dashboardItem = { title: 'Overview', url: '/admin', icon: Target, end: true };

const businessItems = [
  { title: 'Business Intelligence', url: '/admin/business', icon: TrendingUp, end: false },
  { title: 'Credits', url: '/admin/credits', icon: Coins, end: false },
  { title: 'Billing', url: '/admin/billing', icon: DollarSign, end: false },
  { title: 'Referrals', url: '/admin/referrals', icon: UserPlus, end: false },
];

const usersItems = [
  { title: 'Practitioners', url: '/admin/practitioners', icon: Briefcase, end: false },
  { title: 'Users', url: '/admin/users', icon: Users, end: false },
  { title: 'Community Sessions', url: '/admin/community', icon: MessageSquare, end: false },
  { title: 'Shared Sessions', url: '/admin/shared-sessions', icon: Share2, end: false },
];

const systemItems = [
  { title: 'System Health', url: '/admin/system', icon: Activity, end: false },
  { title: 'Error Logs', url: '/admin/errors', icon: AlertCircle, end: false },
  { title: 'Content Management', url: '/admin/content', icon: Database, end: false },
  { title: 'Social Shorts', url: '/admin/shorts', icon: Video, end: false },
  { title: 'Settings', url: '/admin/settings', icon: Settings, end: false },
];

export function AdminSidebar() {
  const { state } = useSidebar();
  const collapsed = state === 'collapsed';

  // Helper to get base NavLink classes
  const getBaseNavLinkClasses = () => {
    return "flex w-full items-center gap-2 overflow-hidden rounded-md p-2 text-left text-sm outline-none transition-all duration-200 [&>span:last-child]:truncate [&>svg]:size-4 [&>svg]:shrink-0";
  };

  const getNavClassName = ({ isActive }: { isActive: boolean }) => {
    const activeClass = isActive ? 'active' : '';

    // All menu items use the same consistent theme highlight
    if (isActive) {
      return `mx-2 mb-1 rounded-lg ${activeClass} bg-accent text-accent-foreground font-semibold border-l-4 border-destructive`;
    }
    return `mx-2 mb-1 rounded-lg hover:bg-accent/50 hover:text-accent-foreground`;
  };

  return (
    <Sidebar className={collapsed ? 'w-14' : 'w-60'} collapsible="icon">
      <div className="p-4 border-b border-sidebar-border">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <Shield className="h-6 w-6 text-destructive" />
            {!collapsed && (
              <h2 className="font-semibold text-sidebar-foreground">Admin</h2>
            )}
          </div>
        </div>
      </div>

      <SidebarContent>
        {/* Dashboard */}
        <SidebarGroup>
          <SidebarGroupContent>
            <SidebarMenu>
              <SidebarMenuItem>
                <NavLink
                  to={dashboardItem.url}
                  end={dashboardItem.end}
                  className={({ isActive }) => cn(
                    getBaseNavLinkClasses(),
                    getNavClassName({ isActive })
                  )}
                >
                  <dashboardItem.icon className="h-4 w-4" />
                  {!collapsed && <span>{dashboardItem.title}</span>}
                </NavLink>
              </SidebarMenuItem>
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        {/* Business Section */}
        <SidebarGroup>
          <SidebarGroupLabel className={collapsed ? 'sr-only' : ''}>
            💼 Business
          </SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {businessItems.map((item) => (
                <SidebarMenuItem key={item.title}>
                  <NavLink
                    to={item.url}
                    end={item.end}
                    className={({ isActive }) => cn(
                      getBaseNavLinkClasses(),
                      getNavClassName({ isActive })
                    )}
                  >
                    <item.icon className="h-4 w-4" />
                    {!collapsed && <span>{item.title}</span>}
                  </NavLink>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        {/* Users Section */}
        <SidebarGroup>
          <SidebarGroupLabel className={collapsed ? 'sr-only' : ''}>
            👥 Users
          </SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {usersItems.map((item) => (
                <SidebarMenuItem key={item.title}>
                  <NavLink
                    to={item.url}
                    end={item.end}
                    className={({ isActive }) => cn(
                      getBaseNavLinkClasses(),
                      getNavClassName({ isActive })
                    )}
                  >
                    <item.icon className="h-4 w-4" />
                    {!collapsed && <span>{item.title}</span>}
                  </NavLink>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        {/* System Section */}
        <SidebarGroup>
          <SidebarGroupLabel className={collapsed ? 'sr-only' : ''}>
            ⚙️ System
          </SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {systemItems.map((item) => (
                <SidebarMenuItem key={item.title}>
                  <NavLink
                    to={item.url}
                    end={item.end}
                    className={({ isActive }) => cn(
                      getBaseNavLinkClasses(),
                      getNavClassName({ isActive })
                    )}
                  >
                    <item.icon className="h-4 w-4" />
                    {!collapsed && <span>{item.title}</span>}
                  </NavLink>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        {/* Main App Link */}
        <SidebarGroup>
          <SidebarGroupLabel className={collapsed ? 'sr-only' : ''}>
            🏠 Account
          </SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              <SidebarMenuItem>
                <NavLink
                  to="/app"
                  className={({ isActive }) => cn(
                    getBaseNavLinkClasses(),
                    getNavClassName({ isActive })
                  )}
                >
                  <Home className="h-4 w-4" />
                  {!collapsed && <span>Return to Home</span>}
                </NavLink>
              </SidebarMenuItem>
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>
    </Sidebar>
  );
}
